import type { Callback } from "shelving";
import { createContext, useContext } from "react";
import { StateError } from "shelving";

const CONTEXT = createContext<Callback | null>(null);

/** Get the retry function in the current context, or `null` if there isn't one. */
export function useOptionalRetry(): Callback | null {
	return useContext(CONTEXT);
}

/** Get the retry function in the current context. */
export function useRetry(): Callback {
	const retry = useOptionalRetry();
	if (!retry) throw new StateError("useRetry() must be used inside <RetryContext>");
	return retry;
}

/** Create a retry context and allow child elements to use a `retry()` function. */
export function Retrier({ retry, children }: { retry: Callback; children: React.ReactNode }): React.ReactElement {
	return <CONTEXT.Provider value={retry}>{children}</CONTEXT.Provider>;
}

/** Button that calls the `retry()` function from the current retry context. */
export function RetryButton({ mods = "" }: { mods?: string }): React.ReactElement | null {
	const retry = useOptionalRetry();
	return retry ? (
		<button type="button" className={`button ${mods}`} onClick={retry}>
			Retry
		</button>
	) : null;
}
